import { createContext, ReactNode, useEffect, useState } from 'react';
import { Button, Col, Container, Row } from 'reactstrap';
import { useAuth0, User } from '@auth0/auth0-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan } from '@fortawesome/free-solid-svg-icons';
import { apiClient } from '../api';
import { authService } from '../api/services/authService';

import { Auth } from '../types';
import { OAuthError } from '@auth0/auth0-react/dist/errors';

export type UserPrefs = Record<string, any>;

interface AuthContextType {
  auth?: Auth;
  setAuth: (auth: Auth) => void;
  userPrefs?: UserPrefs;
  setUserPrefs: (prefs: UserPrefs) => Promise<void>;
  welcomeCompleted: () => Promise<void>;
  logoutUser: () => void;
  getUserInfo: () => Promise<User>;
  getUserInfoById: (uid: string | Array<string>) => Promise<User>;
  getUserInfoByEmail: (email: string) => Promise<User>;
}

const initialContext: AuthContextType = {
  userPrefs: {},
  setAuth: () => {},
  setUserPrefs: () => Promise.resolve(),
  welcomeCompleted: () => Promise.resolve(),
  logoutUser: () => {},
  getUserInfo: () => Promise.resolve({}),
  getUserInfoById: () => Promise.resolve({}),
  getUserInfoByEmail: () => Promise.resolve({}),
};

const AuthContext = createContext<AuthContextType>(initialContext);

export class UserNotKnownError extends Error {
  public email: string;

  constructor(email: string, message: string) {
    super(message);
    this.email = email;
  }
}

const ERRORS: Record<string, string> = { unauthorized: 'XXX' };

function AuthProvider({ children }: { children: ReactNode }) {
  const { user, getAccessTokenSilently, logout, error } = useAuth0();
  const [auth, setAuth] = useState<Auth>();
  const [userPrefs, setPrefs] = useState<UserPrefs | undefined>();

  useEffect(() => {
    if (user) {
      getAccessTokenSilently({ detailedResponse: true })
        .then((token) => {
          const accessToken = token.access_token;
          authService.setAuthToken(accessToken);
          setUser(user, accessToken);
          apiClient.get('/api/preferences').then((response) => setPrefs(response.data));
        })
        .catch(() => {});
    }
  }, [user, getAccessTokenSilently]);

  const setUser = (user: User, accessToken: string) => {
    setAuth({
      org: user['https://coc.lacasa.care/org'] || user['app_metadata']?.org,
      license: user['https://coc.lacasa.care/license'] || user['app_metadata']?.license,
      picture: user.picture || user['user_metadata']?.picture,
      roles: user['https://coc.lacasa.care/roles'] || user.roles || [],
      access: user['https://coc.lacasa.care/access'] || user.access || [],
      sub: user.sub || user.user_id || '',
      email: user.email || '',
      name: user.name || '',
      token: accessToken || '',
      lastLogin: user.updated_at ? new Date(user.updated_at) : new Date(),
    });
  };

  const setUserPrefs = async (prefs: UserPrefs): Promise<void> => {
    setPrefs(prefs);
    if (auth) {
      try {
        await apiClient.post('/api/preferences', prefs);
      } catch (e) {}
    }
  };

  const welcomeCompleted = async (): Promise<void> => {
    if (auth) {
      try {
        await apiClient.post('/api/preferences', { welcomePassed: true });
        const { data: newUserPrefs } = await apiClient.get('/api/preferences');
        setPrefs(newUserPrefs);
      } catch (e) {
        console.error('Error updating preferences:', e);
      }
    }
  };

  const logoutUser = () => {
    setAuth(undefined);
    authService.clearAuthToken();
    logout({ returnTo: window.location.origin });
  };

  const getUserInfo = async () => {
    let response = await apiClient.get('/api/userinfo');
    return await response.data;
  };

  const getUserInfoByEmail = async (email: string) => {
    let response = await apiClient.post('/api/userinfo', { email });
    if (response.data.length === 0)
      throw new UserNotKnownError(email, 'The user is not eligible for a given role.');
    return response.data;
  };

  const getUserInfoById = async (uid: string | Array<string>) => {
    const body = Array.isArray(uid) ? uid.map((v) => ({ uid: v })) : { uid };
    let response = await apiClient.post('/api/userinfo', body);
    if (response.data.length === 0)
      throw new UserNotKnownError(uid.toString(), 'The user is not eligible for a given role.');
    return response.data;
  };

  const AuthError = ({ name, description }: { name: string; description: string }) => (
    <Container>
      <Row>
        <Col sm={12} className="text-center">
          <FontAwesomeIcon icon={faBan} size="5x" className="mt-5 mb-3 text-danger" />
          <div className="text-dark mb-3">{description}</div>
          {ERRORS[name] && <div className="mb-3 text-dark">{ERRORS[name]}</div>}
          <Button onClick={() => (window.location.href = `${window.location.origin}/`)}>
            Return to Login screen
          </Button>
        </Col>
      </Row>
    </Container>
  );

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth,
        userPrefs,
        setUserPrefs,
        welcomeCompleted,
        logoutUser,
        getUserInfo,
        getUserInfoById,
        getUserInfoByEmail,
      }}
    >
      {error ? (
        <AuthError name={(error as OAuthError).error} description={error.message} />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
